/*
 * The wire vocabulary of the client, in one import: the generated core types, the hand-written
 * core event union, and the two aggregates every module widens — the event union `applyEvent`
 * switches over and the request map `socket.send` is typed by (protocol.md §5.2). The documents'
 * union and requests (`core/docs/wire/events.ts`) are core's too and join the aggregates here by
 * name; their generated types stay in `core/docs/wire/docs.gen.ts`, imported where they are used.
 *
 * Widening without an import. core never imports a module tree (architecture.md D21, enforced by
 * eslint), so a module cannot be named here; instead a module's own `events.ts` augments the two
 * interfaces below (`declare module "…/core/wire/index.ts" { interface ModuleEvents { chat: ChatEvent } }`)
 * and the aggregates pick the addition up through the index type. An empty interface indexes to
 * `never`, so the aggregates are exactly the core sets until a module registers.
 */

import type { DocEvent, DocRequests } from "../docs/wire/events.ts"
import type { WireEventLike } from "../store/model.ts"
import type * as core from "./core.gen.ts"
import type { CoreEvent } from "./events.ts"

export * from "./core.gen.ts"
export * from "./events.ts"

/** One entry per module, keyed by module id, each the module's own discriminated event union. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- augmented per module
export interface ModuleEvents {}

/** One entry per module request type: `[request payload, reply payload]`. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- augmented per module
export interface ModuleRequests {}

/**
 * A module's members carry the envelope fields they use; the intersection gives every member the
 * others as optional, so `event.conv` and `event.eventId` exist on the whole union and the gate can
 * read them without narrowing first.
 */
export type WireEvent = CoreEvent | DocEvent | (ModuleEvents[keyof ModuleEvents] & WireEventLike)

/** The five requests core owns (protocol.md §2.5.1); `rt.hello` is sent by the socket itself. */
export interface CoreRequests {
	"rt.hello": [core.Hello, core.HelloResult]
	"rt.bootstrap": [Record<string, never>, core.BootstrapPayload]
	"rt.sync": [core.SyncRequest, core.SyncResult]
	"rt.ping": [Record<string, never>, core.PingResult]
	"rt.focus": [core.Focus, Record<string, never>]
}

export type WireRequest = CoreRequests & DocRequests & ModuleRequests

export type RequestType = keyof WireRequest
export type RequestData<T extends RequestType> = WireRequest[T][0]
export type ReplyData<T extends RequestType> = WireRequest[T][1]

const CORE_REQUEST_TYPE_SET = {
	"rt.hello": true,
	"rt.bootstrap": true,
	"rt.sync": true,
	"rt.ping": true,
	"rt.focus": true,
} as const satisfies Record<keyof CoreRequests, true>

export const CORE_REQUEST_TYPES = Object.keys(
	CORE_REQUEST_TYPE_SET,
) as readonly (keyof CoreRequests)[]

/**
 * An `evt` frame as the reducers see it. The type string is not validated here: a module's types
 * are unknown to core, and an event nobody handles is `applyEvent`'s "unknown" — warned once,
 * never applied (protocol.md §2.4). Absent envelope fields stay absent rather than becoming
 * `undefined` properties, so a member with `eventId?: undefined` matches an ephemeral frame.
 */
export function eventFromEnvelope(env: core.Envelope): WireEvent {
	const event: {
		type: string
		eventId?: number
		conv?: number
		seq?: number
		data: unknown
	} = { type: env.type, data: env.data }
	if (env.eventId !== undefined) event.eventId = env.eventId
	if (env.conv !== undefined) event.conv = env.conv
	if (env.seq !== undefined) event.seq = env.seq
	return event as WireEvent
}
